import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Input,
  Popover,
  Radio,
  Select,
  Spin,
  Table,
  Tabs,
  Tag,
} from "@arco-design/web-react";
import type { TableColumnProps } from "@arco-design/web-react";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronUp,
  Columns3,
  FilePlus2,
  RotateCcw,
  Search,
} from "lucide-react";

import { useCurrentAuthUser } from "../../auth";
import { canUsePermission } from "../../../lib/auth";
import {
  customizationProjectListItineraryOptions,
  customizationProjectListOpportunityBoundOptions,
  customizationProjectListSignTypeOptions,
  customizationProjectListPageSizeOptions,
} from "../config";
import {
  CUSTOMIZATION_PROJECT_LIST_VISIBLE_COLUMNS_STORAGE_KEY,
  customizationProjectListColumnOptions,
  customizationProjectListColumnWidths,
  customizationProjectListDefaultVisibleColumns,
} from "../config/columns";
import {
  WorkbenchColumnSettings,
  loadWorkbenchVisibleColumnsPreference,
  normalizeWorkbenchVisibleColumns,
  saveWorkbenchVisibleColumnsPreference,
} from "../../../shared/ui/WorkbenchColumnSettings";
import { useCustomizationProjectListRuntime } from "../runtime/useCustomizationProjectListRuntime";
import type {
  CustomizationDemandVersionOption,
  CustomizationMyWorkloadDetailType,
  CustomizationProjectListColumnKey,
  CustomizationProjectListFilters,
  CustomizationProjectListItem,
  CustomizationProjectListScope,
  CustomizationProjectListSortBy,
  CustomizationProjectListSortOrder,
} from "../types";
import { CustomizationItineraryPreviewHost } from "./CustomizationItineraryPreviewHost";
import { CustomizationProjectItineraryDrawer } from "./CustomizationProjectItineraryDrawer";
import { CustomizationOpportunityProjectList } from "./CustomizationOpportunityProjectList";
import { CustomizationDemandExcelDetailPanel } from "./CustomizationDemandExcelDetailPanel";
import { formatBeijingCompactTime, isBeijingToday } from "./customizationProjectListTime";
import { SearchHistoryInput } from "../../../shared/ui/SearchHistoryInput";
import {
  rememberSearchHistory,
  SEARCH_HISTORY_KEYS,
} from "../../../lib/searchHistoryStorage";
import "./CustomizationProjectListPage.css";
import "../../../shared/ui/crmListPageLayout.css";

const PROJECT_TABLE_COLUMN_WIDTHS = {
  formed: 64,
  name: 280,
  customerSource: 250,
  sales: 120,
  dispatcher: 120,
  signType: 120,
  currentVersion: 130,
  latestItinerary: 156,
  demandVersions: 120,
  itineraries: 100,
  projectCreatedAt: 156,
  versionUpdatedAt: 156,
  destination: 180,
  people: 100,
} as const;

export const PROJECT_TABLE_SCROLL_WIDTH = Object.values(PROJECT_TABLE_COLUMN_WIDTHS).reduce(
  (total, width) => total + width,
  0,
);

type Props = {
  embedded?: boolean;
  createOnEntry?: boolean;
  fixedFilters?: CustomizationProjectListFilters;
  onCreateSuccess?: (projectId: string) => void;
  onOpenWorkbench?: (projectId: string, options?: { jiDiaoNo?: string }) => void;
  onOpenBudget?: (budgetOrderId: string) => void;
  onOpenOrderHandover?: (
    item: CustomizationProjectListItem,
  ) => void | Promise<void>;
  onDeleteProject?: (projectId: string) => Promise<void>;
  onDeleteDemand?: (
    projectId: string,
    demandId: string,
    options?: { isRootVersion: boolean },
  ) => Promise<void>;
};

/** 定制项目列表页面主体。 */
export function CustomizationProjectListFeaturePage({
  embedded = false,
  createOnEntry = false,
  fixedFilters,
  onCreateSuccess,
  onOpenWorkbench,
  onOpenBudget,
  onOpenOrderHandover,
  onDeleteProject,
  onDeleteDemand,
}: Props) {
  const currentUser = useCurrentAuthUser().data;
  const canCreateProject = canUsePermission(currentUser, "customization.project:create");
  const canListAllProjects = canUsePermission(currentUser, "customization.project:list_all");
  const opportunityId =
    embedded && typeof fixedFilters?.opportunityId === "number"
      ? fixedFilters.opportunityId
      : undefined;
  const topScrollbarRef = useRef<HTMLDivElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const syncingScrollRef = useRef(false);
  const [expandedDemandVersionIds, setExpandedDemandVersionIds] = useState<string[]>([]);
  const [previewItineraryId, setPreviewItineraryId] = useState<string | null>(null);
  const [previewResolving, setPreviewResolving] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<CustomizationProjectListColumnKey[]>(() =>
    loadWorkbenchVisibleColumnsPreference(
      CUSTOMIZATION_PROJECT_LIST_VISIBLE_COLUMNS_STORAGE_KEY,
      customizationProjectListColumnOptions,
      customizationProjectListDefaultVisibleColumns,
    ),
  );
  const runtime = useCustomizationProjectListRuntime({
    createOnEntry: createOnEntry && canCreateProject,
    opportunityId,
    fixedFilters,
    fixedScope: embedded ? "all" : undefined,
    onCreated: onCreateSuccess,
    onBeginItineraryPreview: () => {
      setPreviewItineraryId(null);
      setPreviewResolving(true);
    },
    onPreviewItinerary: (itineraryId) => {
      setPreviewResolving(false);
      setPreviewItineraryId(itineraryId);
    },
    onAbortItineraryPreview: () => {
      setPreviewResolving(false);
      setPreviewItineraryId(null);
    },
  });
  const handleVisibleColumnsChange = useCallback((columns: CustomizationProjectListColumnKey[]) => {
    const normalizedColumns = normalizeWorkbenchVisibleColumns(
      columns,
      customizationProjectListColumnOptions,
      customizationProjectListDefaultVisibleColumns,
    );
    setVisibleColumns(normalizedColumns);
    saveWorkbenchVisibleColumnsPreference(
      CUSTOMIZATION_PROJECT_LIST_VISIBLE_COLUMNS_STORAGE_KEY,
      normalizedColumns,
    );
  }, []);

  const columns = useMemo(
    () =>
      buildProjectListColumns({
        visibleColumns,
        onOpenWorkbench: (projectId) => onOpenWorkbench?.(projectId),
        onOpenDemandVersions: runtime.openDemandVersions,
        onOpenDemandExcelDetail: runtime.openDemandExcelDetail,
        onOpenItineraries: runtime.openItineraries,
        onOpenLatestItinerary: runtime.openLatestItinerary,
        viewerRole: runtime.viewerRole,
        activeSortBy: runtime.sortBy,
        activeSortOrder: runtime.sortOrder,
        onSort: runtime.handleSort,
      }),
    [
      onOpenWorkbench,
      runtime.handleSort,
      runtime.openDemandExcelDetail,
      runtime.openDemandVersions,
      runtime.openItineraries,
      runtime.openLatestItinerary,
      runtime.sortBy,
      runtime.sortOrder,
      runtime.viewerRole,
      visibleColumns,
    ],
  );

  const tableScrollWidth = useMemo(
    () =>
      visibleColumns.reduce(
        (total, key) => total + (customizationProjectListColumnWidths[key] ?? 0),
        0,
      ),
    [visibleColumns],
  );

  useEffect(() => {
    if (runtime.drawer?.type !== "demand_versions" || !runtime.drawer.autoExpandDetail) {
      return;
    }
    if (runtime.demandVersions.length === 0) {
      return;
    }
    setExpandedDemandVersionIds(runtime.demandVersions.map((version) => version.id));
  }, [runtime.demandVersions, runtime.drawer]);

  const findTableScroller = useCallback(() => {
    const tableRoot = tableWrapperRef.current;
    // Arco scroll.x 时真正横向滚动的是 content-inner（overflow-x:auto），
    // content-scroll 为 overflow:hidden，改其 scrollLeft 不会动表格。
    return (
      tableRoot?.querySelector<HTMLElement>(".arco-table-content-inner") ??
      tableRoot?.querySelector<HTMLElement>(".arco-table-body") ??
      tableRoot?.querySelector<HTMLElement>(".arco-table-content-scroll") ??
      null
    );
  }, []);

  const syncScrollLeft = useCallback((source: HTMLElement, target: HTMLElement) => {
    if (syncingScrollRef.current || target.scrollLeft === source.scrollLeft) {
      return;
    }
    syncingScrollRef.current = true;
    target.scrollLeft = source.scrollLeft;
    syncingScrollRef.current = false;
  }, []);

  const handleTopScroll = useCallback(() => {
    const topScrollbar = topScrollbarRef.current;
    const tableScroller = findTableScroller();
    if (!topScrollbar || !tableScroller) {
      return;
    }
    syncScrollLeft(topScrollbar, tableScroller);
  }, [findTableScroller, syncScrollLeft]);

  useEffect(() => {
    const topScrollbar = topScrollbarRef.current;
    const tableScroller = findTableScroller();
    if (!topScrollbar || !tableScroller) {
      return undefined;
    }
    const handleTableScroll = () => syncScrollLeft(tableScroller, topScrollbar);
    tableScroller.addEventListener("scroll", handleTableScroll);
    topScrollbar.scrollLeft = tableScroller.scrollLeft;
    return () => {
      tableScroller.removeEventListener("scroll", handleTableScroll);
    };
  }, [findTableScroller, runtime.items.length, runtime.loading, syncScrollLeft]);

  const submitSearch = () => {
    rememberSearchHistory(
      SEARCH_HISTORY_KEYS.customizationKeyword,
      runtime.draftFilters.keyword ?? "",
    );
    rememberSearchHistory(
      SEARCH_HISTORY_KEYS.customizationOrganizationName,
      runtime.draftFilters.organizationName ?? "",
    );
    rememberSearchHistory(
      SEARCH_HISTORY_KEYS.customizationDestination,
      runtime.draftFilters.destination ?? "",
    );
    runtime.handleSearch();
  };

  return (
    <main
      className={`customization-project-list-page${embedded ? " is-embedded" : ""}`}
    >
      {embedded ? (
        <Input.Search
          aria-label="搜索关联定制"
          placeholder="搜索定制编号或名称"
          style={{ width: "100%", maxWidth: 420 }}
          allowClear
          maxLength={100}
          value={runtime.draftFilters.keyword ?? ""}
          onChange={(keyword) =>
            runtime.setDraftFilters((current) => ({ ...current, keyword }))
          }
          onSearch={runtime.handleSearch}
          onClear={runtime.handleReset}
        />
      ) : (
        <section
          className="crm-list-filter-panel customization-project-list-filter-panel"
          role="search"
          aria-label="定制筛选"
          data-layout="two-tier-responsive"
        >
          <form
            className="crm-list-filter-fields customization-project-list-filter-fields"
            onSubmit={(event) => {
              event.preventDefault();
              submitSearch();
            }}
          >
            <div className="crm-list-filter-primary">
              <label className="crm-list-field crm-list-field--lg">
                <span>需求名称/编号</span>
                <SearchHistoryInput
                  storageKey={SEARCH_HISTORY_KEYS.customizationKeyword}
                  placeholder="需求名称/编号"
                  value={runtime.draftFilters.keyword ?? ""}
                  onPressEnter={submitSearch}
                  onChange={(keyword) =>
                    runtime.setDraftFilters((current) => ({ ...current, keyword }))
                  }
                />
              </label>
              <label className="crm-list-field crm-list-field--md">
                <span>所属院校</span>
                <SearchHistoryInput
                  storageKey={SEARCH_HISTORY_KEYS.customizationOrganizationName}
                  placeholder="所属院校"
                  value={runtime.draftFilters.organizationName ?? ""}
                  onPressEnter={submitSearch}
                  onChange={(organizationName) =>
                    runtime.setDraftFilters((current) => ({ ...current, organizationName }))
                  }
                />
              </label>
              <label className="crm-list-field crm-list-field--md">
                <span>目的地</span>
                <SearchHistoryInput
                  storageKey={SEARCH_HISTORY_KEYS.customizationDestination}
                  placeholder="目的地"
                  value={runtime.draftFilters.destination ?? ""}
                  onPressEnter={submitSearch}
                  onChange={(destination) =>
                    runtime.setDraftFilters((current) => ({ ...current, destination }))
                  }
                />
              </label>
              <div className="crm-list-field crm-list-field--md">
                <span>签单类型</span>
                <Select
                  allowClear
                  placeholder="签单类型"
                  value={runtime.draftFilters.signType}
                  getPopupContainer={() => document.body}
                  onChange={(signType) =>
                    runtime.setDraftFilters((current) => ({
                      ...current,
                      signType: typeof signType === "string" ? signType : undefined,
                    }))
                  }
                >
                  {customizationProjectListSignTypeOptions.map((item) => (
                    <Select.Option key={item.value} value={item.value}>
                      {item.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div className="crm-list-field crm-list-field--sm">
                <span>行程</span>
                <Select
                  allowClear
                  placeholder="行程"
                  value={
                    runtime.draftFilters.hasItinerary === true
                      ? "true"
                      : runtime.draftFilters.hasItinerary === false
                        ? "false"
                        : undefined
                  }
                  getPopupContainer={() => document.body}
                  onChange={(hasItinerary) =>
                    runtime.setDraftFilters((current) => ({
                      ...current,
                      hasItinerary:
                        hasItinerary === "true"
                          ? true
                          : hasItinerary === "false"
                            ? false
                            : undefined,
                    }))
                  }
                >
                  {customizationProjectListItineraryOptions.map((item) => (
                    <Select.Option key={item.value} value={item.value}>
                      {item.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div className="crm-list-field crm-list-field--sm">
                <span>商机归属</span>
                <Select
                  allowClear
                  placeholder="商机归属"
                  value={
                    runtime.draftFilters.hasOpportunity === true
                      ? "true"
                      : runtime.draftFilters.hasOpportunity === false
                        ? "false"
                        : undefined
                  }
                  getPopupContainer={() => document.body}
                  onChange={(hasOpportunity) =>
                    runtime.setDraftFilters((current) => ({
                      ...current,
                      hasOpportunity:
                        hasOpportunity === "true"
                          ? true
                          : hasOpportunity === "false"
                            ? false
                            : undefined,
                    }))
                  }
                >
                  {customizationProjectListOpportunityBoundOptions.map((item) => (
                    <Select.Option key={item.value} value={item.value}>
                      {item.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>
          </form>
          <div className="crm-list-filter-actions">
            <Button
              htmlType="button"
              icon={<RotateCcw size={16} />}
              onClick={() => runtime.handleReset()}
            >
              重置
            </Button>
            <Button
              htmlType="button"
              type="primary"
              icon={<Search size={16} />}
              onClick={submitSearch}
            >
              查询
            </Button>
          </div>
        </section>
      )}
      {runtime.errorMessage ? (
        <Alert
          className="customization-project-list-alert"
          type="error"
          showIcon
          content={runtime.errorMessage}
        />
      ) : null}
      {embedded ? null : runtime.mutationErrorMessage ? (
        <Alert
          className="customization-project-list-alert"
          type="error"
          showIcon
          content={runtime.mutationErrorMessage}
        />
      ) : null}

      <section
        className={`customization-project-list-panel${embedded ? " is-embedded" : ""}`}
      >
        {embedded ? null : <div className="customization-project-list-section-header customization-project-list-table-header">
          <div className="customization-project-list-title-row">
            <h2>需求列表</h2>
            <div className="customization-project-list-scope-tabs">
              {(() => {
                const myWorkloadTabActive = runtime.drawer?.type === "my_workload";
                const scopeTabValue = myWorkloadTabActive ? undefined : runtime.scope;
                const handleScopeTabChange = (value: string | number) => {
                  if (myWorkloadTabActive) {
                    setExpandedDemandVersionIds([]);
                    runtime.closeDrawer();
                  }
                  runtime.handleScopeChange(value as CustomizationProjectListScope);
                };
                return (
                  <>
                    <Radio.Group
                      type="button"
                      value={scopeTabValue}
                      onChange={handleScopeTabChange}
                    >
                      <Radio value="mine">我的定制</Radio>
                    </Radio.Group>
                    <Button
                      type="outline"
                      className={`customization-project-list-without-plan-tab${
                        myWorkloadTabActive ? " is-active" : ""
                      }`}
                      aria-pressed={myWorkloadTabActive}
                      onClick={() => runtime.openMyWorkload()}
                    >
                      我的未接单
                    </Button>
                    {canListAllProjects ? (
                      <Radio.Group
                        type="button"
                        value={scopeTabValue}
                        onChange={handleScopeTabChange}
                      >
                        <Radio value="all">全部定制</Radio>
                      </Radio.Group>
                    ) : null}
                  </>
                );
              })()}
            </div>
          </div>
          <div className="customization-project-list-table-actions crm-list-table-actions">
            <div className="customization-project-list-statline">
              共 {runtime.total} 个定制项目
            </div>
            <Button
              type="primary"
              icon={<FilePlus2 size={16} />}
              loading={runtime.creating}
              disabled={!canCreateProject}
              onClick={runtime.handleCreateProject}
            >
              录入定制需求
            </Button>
            <Popover
              position="br"
              trigger="click"
              content={
                <WorkbenchColumnSettings
                  options={customizationProjectListColumnOptions}
                  visibleColumns={visibleColumns}
                  onChange={handleVisibleColumnsChange}
                />
              }
            >
              <Button icon={<Columns3 size={14} />}>列设置</Button>
            </Popover>
          </div>
        </div>}
        {embedded ? (
          <CustomizationOpportunityProjectList
            items={runtime.items}
            loading={runtime.loading}
            page={runtime.page}
            pageSize={runtime.pageSize}
            total={runtime.total}
            onPageChange={runtime.handlePageChange}
            onPageSizeChange={runtime.handlePageSizeChange}
            onOpenWorkbench={(projectId) => onOpenWorkbench?.(projectId)}
            onOpenBudget={onOpenBudget}
            onOpenOrderHandover={onOpenOrderHandover}
            onOpenItineraryPreview={(itineraryId) => {
              setPreviewResolving(false);
              setPreviewItineraryId(itineraryId);
            }}
            onDeleteProject={onDeleteProject}
            onDeleteDemand={onDeleteDemand}
          />
        ) : (
          <>
            <div
              ref={topScrollbarRef}
              className="customization-project-list-top-scrollbar"
              data-testid="customization-project-top-scrollbar"
              aria-label="定制列表顶部横向滚动条"
              onScroll={handleTopScroll}
            >
              <div
                className="customization-project-list-top-scrollbar-inner"
                style={{ width: tableScrollWidth }}
              />
            </div>
            <div ref={tableWrapperRef}>
              <Table
                rowKey="id"
                columns={columns}
                data={runtime.items}
                loading={runtime.loading}
                border={{ wrapper: true, cell: false }}
                scroll={{ x: tableScrollWidth }}
                pagination={{
                  current: runtime.page,
                  pageSize: runtime.pageSize,
                  total: runtime.total,
                  sizeCanChange: true,
                  sizeOptions: customizationProjectListPageSizeOptions.map((item) => item.value),
                  showTotal: true,
                  onChange: runtime.handlePageChange,
                  onPageSizeChange: runtime.handlePageSizeChange,
                }}
              />
            </div>
          </>
        )}
      </section>

      <Drawer
        className="customization-project-demand-versions-drawer customization-my-workload-drawer"
        wrapClassName="customization-project-demand-versions-drawer-wrapper"
        title="我的未接单"
        width={720}
        visible={runtime.drawer?.type === "my_workload"}
        footer={null}
        zIndex={1200}
        getPopupContainer={() => document.body}
        onCancel={() => runtime.closeDrawer()}
      >
        <Tabs
          type="rounded"
          activeTab={runtime.myWorkloadTab}
          onChange={(key) =>
            runtime.handleMyWorkloadTabChange(key as CustomizationMyWorkloadDetailType)
          }
          className="customization-my-workload-tabs"
        >
          <Tabs.TabPane
            key="pending"
            title={
              <span className="customization-my-workload-tab-label is-pending">
                未接单
                <em>{runtime.myWorkload.pendingOrderCount}</em>
              </span>
            }
          />
          <Tabs.TabPane
            key="following"
            title={
              <span className="customization-my-workload-tab-label is-following">
                在跟单
                <em>{runtime.myWorkload.followingOrderCount}</em>
              </span>
            }
          />
          <Tabs.TabPane
            key="unfinished"
            title={
              <span className="customization-my-workload-tab-label is-unfinished">
                未完团
                <em>{runtime.myWorkload.unfinishedGroupCount}</em>
              </span>
            }
          />
          <Tabs.TabPane
            key="finished"
            title={
              <span className="customization-my-workload-tab-label is-finished">
                已完团
                <em>{runtime.myWorkload.finishedGroupCount}</em>
              </span>
            }
          />
        </Tabs>
        <Spin loading={runtime.drawerLoading} style={{ width: "100%" }}>
          {runtime.drawerErrorMessage ? (
            <Alert type="error" showIcon content={runtime.drawerErrorMessage} />
          ) : runtime.myWorkloadTab === "pending" || runtime.myWorkloadTab === "following" ? (
            runtime.myWorkload.orderItems.length > 0 ? (
              <div className="customization-project-drawer-list" aria-label="我的未接单明细">
                {runtime.myWorkload.orderItems.map((item) => {
                  const openWorkbench = () =>
                    onOpenWorkbench?.(item.jidiaoNo, { jiDiaoNo: item.jidiaoNo });
                  const statusLabel =
                    runtime.myWorkloadTab === "pending" ? "未接单" : "在跟单";
                  return (
                    <article
                      key={item.jidiaoNo}
                      className="customization-demand-version-card"
                      aria-label={`定制 ${item.jidiaoNo}`}
                    >
                      <header className="customization-demand-version-card-header">
                        <div className="customization-demand-version-card-heading">
                          <div className="customization-demand-version-card-heading-main">
                            <Button
                              type="text"
                              className="customization-demand-version-card-jidiao-link"
                              aria-label={`进入需求版本 ${item.jidiaoNo} 工作台`}
                              onClick={openWorkbench}
                            >
                              <strong className="customization-demand-version-card-jidiao">
                                {item.jidiaoNo}
                              </strong>
                            </Button>
                          </div>
                        </div>
                        <span
                          className={`customization-demand-version-card-plan${
                            runtime.myWorkloadTab === "pending" ? " is-no" : " is-yes"
                          }`}
                        >
                          {statusLabel}
                        </span>
                      </header>
                      <Button
                        type="text"
                        className="customization-demand-version-card-name-link"
                        aria-label={`进入${item.businessOpportunityName || item.jidiaoNo}工作台`}
                        onClick={openWorkbench}
                      >
                        <h3 className="customization-demand-version-card-name">
                          {item.businessOpportunityName || item.jidiaoNo}
                        </h3>
                      </Button>
                      <div className="customization-demand-version-card-facts">
                        {item.startDateLabel ? (
                          <div className="customization-demand-version-card-fact">
                            <span className="customization-demand-version-card-fact-label">出发</span>
                            <strong>{item.startDateLabel}</strong>
                          </div>
                        ) : null}
                        <div className="customization-demand-version-card-fact">
                          <span className="customization-demand-version-card-fact-label">销售</span>
                          <strong>{item.salesName?.trim() || "--"}</strong>
                        </div>
                        {item.lastPriceTimeLabel ? (
                          <div className="customization-demand-version-card-fact">
                            <span className="customization-demand-version-card-fact-label">要方案</span>
                            <strong>{item.lastPriceTimeLabel}</strong>
                          </div>
                        ) : null}
                      </div>
                      <div className="customization-demand-version-card-meta">
                        <div
                          className="customization-demand-version-card-chips"
                          aria-label="版本摘要"
                        >
                          <span className="customization-demand-version-card-chip">
                            定制号 {item.jidiaoNo.split("_")[0] || item.jidiaoNo}
                          </span>
                          {item.lastPriceTimeLabel ? (
                            <span className="customization-demand-version-card-chip">
                              {item.lastPriceTimeLabel}要方案
                            </span>
                          ) : null}
                          {item.startDateLabel ? (
                            <span className="customization-demand-version-card-chip">
                              {item.startDateLabel}出发
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <Empty
                description={
                  runtime.myWorkloadTab === "pending" ? "暂无未接单" : "暂无在跟单"
                }
              />
            )
          ) : runtime.myWorkload.groupItems.length > 0 ? (
            <div className="customization-project-drawer-list" aria-label="我的团期明细">
              {runtime.myWorkload.groupItems.map((item) => (
                <article
                  key={item.teamId}
                  className="customization-demand-version-card"
                  aria-label={`团 ${item.teamId}`}
                >
                  <header className="customization-demand-version-card-header">
                    <div className="customization-demand-version-card-heading">
                      <div className="customization-demand-version-card-heading-main">
                        <strong className="customization-demand-version-card-jidiao">
                          {item.teamId}
                        </strong>
                        {item.jidiaoBatchNo ? (
                          <span className="customization-demand-version-card-company">
                            【{item.jidiaoBatchNo}】
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <span
                      className={`customization-demand-version-card-plan${
                        runtime.myWorkloadTab === "unfinished" ? " is-no" : " is-yes"
                      }`}
                    >
                      {runtime.myWorkloadTab === "unfinished" ? "未完团" : "已完团"}
                    </span>
                  </header>
                  <h3 className="customization-demand-version-card-name customization-my-workload-group-title">
                    {item.title}
                  </h3>
                  <div className="customization-demand-version-card-facts">
                    {item.startDateLabel ? (
                      <div className="customization-demand-version-card-fact">
                        <span className="customization-demand-version-card-fact-label">出发</span>
                        <strong>{item.startDateLabel}</strong>
                      </div>
                    ) : null}
                    {item.endDateLabel ? (
                      <div className="customization-demand-version-card-fact">
                        <span className="customization-demand-version-card-fact-label">结束</span>
                        <strong>{item.endDateLabel}</strong>
                      </div>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <Empty
              description={
                runtime.myWorkloadTab === "unfinished" ? "暂无未完团" : "暂无已完团"
              }
            />
          )}
        </Spin>
      </Drawer>

      <Drawer
        className="customization-project-demand-versions-drawer"
        wrapClassName="customization-project-demand-versions-drawer-wrapper"
        title="全部需求版本"
        width={720}
        visible={runtime.drawer?.type === "demand_versions"}
        footer={null}
        zIndex={1200}
        getPopupContainer={() => document.body}
        onCancel={() => {
          setExpandedDemandVersionIds([]);
          runtime.closeDrawer();
        }}
        afterClose={() => setExpandedDemandVersionIds([])}
      >
        <Spin loading={runtime.drawerLoading} style={{ width: "100%" }}>
          {runtime.drawerErrorMessage ? (
            <Alert type="error" showIcon content={runtime.drawerErrorMessage} />
          ) : runtime.demandVersions.length > 0 && runtime.drawer?.type === "demand_versions" ? (
            <div className="customization-project-drawer-list">
              {runtime.demandVersions.map((version, index) => (
                <DemandVersionOverviewCard
                  key={version.id}
                  index={runtime.demandVersions.length - index}
                  projectId={runtime.drawer.projectId}
                  version={version}
                  projectContext={{
                    name: runtime.drawer.projectName,
                    projectCode: runtime.drawer.projectCode,
                    legacyId: runtime.drawer.legacyId,
                    operatingCompany: runtime.drawer.operatingCompany,
                    salesUserName: runtime.drawer.salesUserName,
                    dispatcherUserName: runtime.drawer.dispatcherUserName,
                    organizationNameSnapshot: runtime.drawer.organizationNameSnapshot,
                    customerName: runtime.drawer.customerName,
                    contactName: runtime.drawer.contactName,
                    customerType: runtime.drawer.customerType,
                    tripType: runtime.drawer.tripType,
                    signType: runtime.drawer.signType,
                    dealProgress: runtime.drawer.dealProgress,
                    projectCreatedAt: runtime.drawer.projectCreatedAt,
                  }}
                  expanded={expandedDemandVersionIds.includes(version.id)}
                  onOpenWorkbench={(projectId, options) => onOpenWorkbench?.(projectId, options)}
                  onOpenItineraryPreview={(itineraryId) => {
                    setPreviewResolving(false);
                    setPreviewItineraryId(itineraryId);
                  }}
                  onToggleDetail={() => {
                    setExpandedDemandVersionIds((current) =>
                      current.includes(version.id)
                        ? current.filter((id) => id !== version.id)
                        : [...current, version.id],
                    );
                  }}
                />
              ))}
            </div>
          ) : (
            <Empty description="暂无需求版本" />
          )}
        </Spin>
      </Drawer>
      <Drawer
        className="customization-project-demand-excel-detail-drawer"
        wrapClassName="customization-project-demand-versions-drawer-wrapper"
        title={
          runtime.drawer?.type === "demand_excel_detail"
            ? `需求详表 ${runtime.drawer.versionNumber?.trim() || ""}`.trim()
            : "需求详表"
        }
        width={720}
        visible={runtime.drawer?.type === "demand_excel_detail"}
        footer={null}
        zIndex={1200}
        getPopupContainer={() => document.body}
        onCancel={runtime.closeDrawer}
      >
        {runtime.drawer?.type === "demand_excel_detail" ? (
          <CustomizationDemandExcelDetailPanel
            projectId={runtime.drawer.projectId}
            view="preview"
            version={{
              id: runtime.drawer.demandId,
              versionNumber: runtime.drawer.versionNumber?.trim() || "未生成",
              versionSeq: runtime.drawer.versionSeq ?? 1,
              createdAt: runtime.drawer.projectCreatedAt ?? "",
              isCurrent: true,
            }}
            projectContext={{
              name: runtime.drawer.projectName,
              projectCode: runtime.drawer.projectCode,
              legacyId: runtime.drawer.legacyId,
              operatingCompany: runtime.drawer.operatingCompany,
              salesUserName: runtime.drawer.salesUserName,
              dispatcherUserName: runtime.drawer.dispatcherUserName,
              organizationNameSnapshot: runtime.drawer.organizationNameSnapshot,
              customerName: runtime.drawer.customerName,
              contactName: runtime.drawer.contactName,
              customerType: runtime.drawer.customerType,
              tripType: runtime.drawer.tripType,
              signType: runtime.drawer.signType,
              dealProgress: runtime.drawer.dealProgress,
              projectCreatedAt: runtime.drawer.projectCreatedAt,
            }}
          />
        ) : null}
      </Drawer>
      <CustomizationProjectItineraryDrawer
        visible={runtime.drawer?.type === "itineraries"}
        fallbackProjectName={
          runtime.drawer?.type === "itineraries" ? runtime.drawer.projectName : ""
        }
        data={runtime.itineraryPage}
        loading={runtime.drawer?.type === "itineraries" && runtime.drawerLoading}
        errorMessage={
          runtime.drawer?.type === "itineraries" ? runtime.drawerErrorMessage : ""
        }
        onClose={runtime.closeDrawer}
        onPageChange={runtime.handleItineraryDrawerPageChange}
        onPreviewItinerary={(itineraryId) => {
          setPreviewResolving(false);
          setPreviewItineraryId(itineraryId);
        }}
      />
      <CustomizationItineraryPreviewHost
        itineraryId={previewItineraryId}
        resolving={previewResolving}
        onClose={() => {
          setPreviewResolving(false);
          setPreviewItineraryId(null);
        }}
      />
    </main>
  );
}

/** 对齐旧系统 MySelfDo 的需求版本概览卡片：「详细」展开 onlineShowExcelSelfDo 风格表格。 */
function DemandVersionOverviewCard({
  index,
  showIndex = true,
  projectId,
  version,
  projectContext,
  expanded,
  onOpenWorkbench,
  onOpenItineraryPreview,
  onToggleDetail,
}: {
  index: number;
  showIndex?: boolean;
  projectId: string;
  version: CustomizationDemandVersionOption;
  projectContext: {
    name: string;
    projectCode?: string;
    legacyId?: string;
    operatingCompany?: string;
    salesUserName?: string;
    dispatcherUserName?: string;
    organizationNameSnapshot?: string;
    customerName?: string;
    contactName?: string;
    customerType?: string;
    tripType?: string;
    signType?: string;
    dealProgress?: string;
    projectCreatedAt?: string;
  };
  expanded: boolean;
  onOpenWorkbench: (projectId: string, options?: { jiDiaoNo?: string }) => void;
  onOpenItineraryPreview: (itineraryId: string) => void;
  onToggleDetail: () => void;
}) {
  const versionLabel = version.versionNumber || `V${version.versionSeq}`;
  const orderVersionKey =
    version.orderVersionKey ||
    `${projectContext.projectCode || projectContext.legacyId || "--"}_${version.versionSeq}`;
  const companyLabel = projectContext.operatingCompany?.trim() || "花儿旅行社";
  const hasItinerary = Boolean(version.hasItinerary || (version.itineraryCount ?? 0) > 0);
  const itineraryCount = version.itineraryCount ?? (hasItinerary ? 1 : 0);
  const itineraryTitle = version.itineraryTitle?.trim() || "";
  const latestItineraryId = version.latestItineraryId?.trim() || "";
  const projectTitle = projectContext.name?.trim() || "--";
  const peopleText =
    version.studentCount == null && version.teacherCount == null
      ? null
      : `${version.studentCount ?? 0}+${version.teacherCount ?? 0}人`;
  const locationParts = [
    ...(version.provinceNames ?? []),
    ...(version.stayAreaNames ?? []),
    projectContext.organizationNameSnapshot,
    projectContext.customerName,
  ]
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value));
  const locationText = Array.from(new Set(locationParts)).join(" · ");
  const projectCode = projectContext.projectCode || projectContext.legacyId || "--";
  const openVersionWorkbench = () => {
    onOpenWorkbench(projectId, { jiDiaoNo: orderVersionKey });
  };

  return (
    <article
      className={`customization-demand-version-card${expanded ? " is-expanded" : ""}`}
      aria-label={`需求版本 ${versionLabel}`}
    >
      <header className="customization-demand-version-card-header">
        {showIndex ? (
          <div className="customization-demand-version-card-index" aria-hidden>
            {index}
          </div>
        ) : null}
        <div className="customization-demand-version-card-heading">
          <div className="customization-demand-version-card-heading-main">
            <Button
              type="text"
              className="customization-demand-version-card-jidiao-link"
              aria-label={`进入需求版本 ${orderVersionKey} 工作台`}
              onClick={openVersionWorkbench}
            >
              <strong className="customization-demand-version-card-jidiao">{orderVersionKey}</strong>
            </Button>
            <span className="customization-demand-version-card-company">【{companyLabel}】</span>
          </div>
          <time className="customization-demand-version-card-time">{version.createdAt || "--"}</time>
        </div>
        <span
          className={
            hasItinerary
              ? "customization-demand-version-card-plan is-yes"
              : "customization-demand-version-card-plan is-no"
          }
        >
          {hasItinerary ? "有方案" : "无方案"}
        </span>
      </header>

      <Button
        type="text"
        className="customization-demand-version-card-name-link"
        aria-label={`进入${projectTitle}（${orderVersionKey}）工作台`}
        onClick={openVersionWorkbench}
      >
        <h3 className="customization-demand-version-card-name">{projectTitle}</h3>
      </Button>

      <div className="customization-demand-version-card-facts">
        {version.departureStartDate ? (
          <div className="customization-demand-version-card-fact">
            <span className="customization-demand-version-card-fact-label">出发</span>
            <strong>
              {version.departureStartDate.replaceAll("-", "/")}
              {peopleText ? ` · ${peopleText}` : ""}
            </strong>
          </div>
        ) : null}
        <div className="customization-demand-version-card-fact">
          <span className="customization-demand-version-card-fact-label">销售</span>
          <strong>{projectContext.salesUserName?.trim() || "--"}</strong>
        </div>
        <div className="customization-demand-version-card-fact">
          <span className="customization-demand-version-card-fact-label">计调</span>
          <strong>{projectContext.dispatcherUserName?.trim() || "--"}</strong>
        </div>
        {locationText ? (
          <div className="customization-demand-version-card-fact is-wide">
            <span className="customization-demand-version-card-fact-label">目的地</span>
            <strong>{locationText}</strong>
          </div>
        ) : null}
      </div>

      <div className="customization-demand-version-card-meta">
        <div className="customization-demand-version-card-chips" aria-label="版本摘要">
          <span className="customization-demand-version-card-chip">定制号 {projectCode}</span>
          <span className="customization-demand-version-card-chip">
            {versionLabel} · 第 {version.versionSeq} 版
          </span>
          <span className="customization-demand-version-card-chip">
            {hasItinerary ? `${itineraryCount} 方案` : "0 方案"}
          </span>
          <span className="customization-demand-version-card-chip">
            {version.finalizedBudgetCount ?? 0} 核价
          </span>
          {projectContext.customerType ? (
            <span className="customization-demand-version-card-chip is-accent">
              {projectContext.customerType}
            </span>
          ) : null}
        </div>
        <footer className="customization-demand-version-card-actions">
          <Button
            type={expanded ? "outline" : "primary"}
            size="mini"
            icon={expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            aria-label={expanded ? `收起 ${versionLabel} 详细` : `查看 ${versionLabel} 详细`}
            onClick={onToggleDetail}
          >
            {expanded ? "收起" : "详细"}
          </Button>
        </footer>
      </div>

      {hasItinerary && itineraryTitle && latestItineraryId ? (
        <button
          type="button"
          className="customization-demand-version-card-route"
          aria-label={`打开行程 ${itineraryTitle}`}
          onClick={() => onOpenItineraryPreview(latestItineraryId)}
        >
          <span className="customization-demand-version-card-route-label">行程</span>
          <strong>{itineraryTitle}</strong>
        </button>
      ) : hasItinerary && itineraryTitle ? (
        <div className="customization-demand-version-card-route" aria-label="行程名称">
          <span className="customization-demand-version-card-route-label">行程</span>
          <strong>{itineraryTitle}</strong>
        </div>
      ) : null}

      {expanded ? (
        <div className="customization-demand-version-card-detail">
          <CustomizationDemandExcelDetailPanel
            projectId={projectId}
            version={version}
            view="preview"
            projectContext={projectContext}
          />
        </div>
      ) : null}
    </article>
  );
}

/** 构建定制项目列表列配置。 */
function buildProjectListColumns({
  visibleColumns,
  onOpenWorkbench,
  onOpenDemandVersions,
  onOpenDemandExcelDetail,
  onOpenItineraries,
  onOpenLatestItinerary,
  viewerRole,
  activeSortBy,
  activeSortOrder,
  onSort,
}: {
  visibleColumns: CustomizationProjectListColumnKey[];
  onOpenWorkbench: (projectId: string) => void;
  onOpenDemandVersions: (item: CustomizationProjectListItem) => void | Promise<void>;
  onOpenDemandExcelDetail: (
    item: Pick<CustomizationProjectListItem, "id" | "effectiveDemandId" | "versionNumber">,
  ) => void;
  onOpenItineraries: (
    item: Pick<CustomizationProjectListItem, "id" | "name" | "itineraryCount">,
  ) => void;
  onOpenLatestItinerary: (
    item: Pick<CustomizationProjectListItem, "latestItineraryId">,
  ) => void;
  viewerRole: "dispatcher" | "sales";
  activeSortBy: CustomizationProjectListSortBy;
  activeSortOrder: CustomizationProjectListSortOrder;
  onSort: (
    sortBy: CustomizationProjectListSortBy,
    sortOrder: CustomizationProjectListSortOrder,
  ) => void;
}): TableColumnProps<CustomizationProjectListItem>[] {
  const visibleSet = new Set(visibleColumns);
  const columnByKey = new Map<
    CustomizationProjectListColumnKey,
    TableColumnProps<CustomizationProjectListItem>
  >();

  if (visibleSet.has("formed")) {
    columnByKey.set("formed", {
      title: "团",
      dataIndex: "isFormed",
      width: PROJECT_TABLE_COLUMN_WIDTHS.formed,
      render: (_, item) => (
        <span
          className={`customization-project-formed-tag ${
            item.isFormed
              ? "customization-project-formed-tag--active"
              : "customization-project-formed-tag--inactive"
          }`}
          title={item.isFormed ? "已成团" : "未成团"}
        >
          团
        </span>
      ),
    });
  }
  if (visibleSet.has("name")) {
    columnByKey.set("name", {
      title: "定制名称",
      dataIndex: "name",
      width: PROJECT_TABLE_COLUMN_WIDTHS.name,
      render: (_, item) => (
        <div className="customization-project-name-cell">
          <Button
            type="text"
            className="customization-project-name-link"
            aria-label={`进入${item.name || item.projectCode || "定制项目"}工作台`}
            onClick={() => onOpenWorkbench(item.id)}
          >
            {item.name || "--"}
          </Button>
          <span>{item.legacyId || item.projectCode || "--"}</span>
          {item.effectiveStateConflict ? (
            <Tag color="orangered">
              {buildEffectiveStateConflictText(item.effectiveStateConflict)}
            </Tag>
          ) : null}
        </div>
      ),
    });
  }
  if (visibleSet.has("customerSource")) {
    columnByKey.set("customerSource", {
      title: "客户来源",
      width: PROJECT_TABLE_COLUMN_WIDTHS.customerSource,
      render: (_, item) => (
        <div className="customization-project-person-cell">
          <span>{buildCustomerSourceText(item)}</span>
          <span className="customization-project-muted">{item.contactName || "联系人待填"}</span>
        </div>
      ),
    });
  }
  if (visibleSet.has("sales")) {
    columnByKey.set("sales", {
      title: "销售",
      dataIndex: "salesUserName",
      width: PROJECT_TABLE_COLUMN_WIDTHS.sales,
      render: (_, item) => item.salesUserName || "--",
    });
  }
  if (visibleSet.has("dispatcher")) {
    columnByKey.set("dispatcher", {
      title: "计调",
      dataIndex: "dispatcherUserName",
      width: PROJECT_TABLE_COLUMN_WIDTHS.dispatcher,
      render: (_, item) => item.dispatcherUserName || "--",
    });
  }
  if (visibleSet.has("signType")) {
    columnByKey.set("signType", {
      title: "签单类型",
      dataIndex: "signType",
      width: PROJECT_TABLE_COLUMN_WIDTHS.signType,
      render: (_, item) => item.signType?.trim() || "--",
    });
  }
  if (visibleSet.has("version")) {
    columnByKey.set("version", {
      title: "当前版本",
      dataIndex: "versionNumber",
      width: PROJECT_TABLE_COLUMN_WIDTHS.currentVersion,
      render: (_, item) => {
        const versionLabel = item.versionNumber || "未生成";
        const demandId = item.effectiveDemandId?.trim();
        if (!demandId || versionLabel === "未生成") {
          return <Tag color="arcoblue">{versionLabel}</Tag>;
        }
        return (
          <button
            type="button"
            className="customization-project-version-link"
            aria-label={`查看 ${versionLabel} 需求详表`}
            onClick={(event) => {
              event.stopPropagation();
              onOpenDemandExcelDetail(item);
            }}
          >
            <Tag color="arcoblue">{versionLabel}</Tag>
          </button>
        );
      },
    });
  }
  if (visibleSet.has("latestItinerary")) {
    columnByKey.set("latestItinerary", {
      title: (
        <SortableHeader
          label="最近行程"
          field="latest_itinerary_updated_at"
          activeField={activeSortBy}
          activeDirection={activeSortOrder}
          onSort={onSort}
        />
      ),
      dataIndex: "latestItineraryUpdatedAt",
      width: PROJECT_TABLE_COLUMN_WIDTHS.latestItinerary,
      render: (_, item) => {
        const highlighted =
          viewerRole === "sales" && isBeijingToday(item.latestItineraryUpdatedAt);
        const label = formatBeijingCompactTime(item.latestItineraryUpdatedAt);
        const itineraryId = item.latestItineraryId?.trim();
        if (!itineraryId || label === "--") {
          return renderCompactTime(item.latestItineraryUpdatedAt, highlighted);
        }
        return (
          <Button
            type="text"
            size="small"
            className={`customization-project-time-link${
              highlighted ? " customization-project-time--today" : ""
            }`}
            aria-label={`打开最近行程 ${label}`}
            onClick={() => onOpenLatestItinerary(item)}
          >
            {label}
          </Button>
        );
      },
    });
  }
  if (visibleSet.has("demandVersionCount")) {
    columnByKey.set("demandVersionCount", {
      title: "需求版本数",
      width: PROJECT_TABLE_COLUMN_WIDTHS.demandVersions,
      render: (_, item) => (
        <Button
          type="text"
          size="small"
          aria-label={`查看 ${item.demandVersionCount ?? 0} 个需求版本`}
          onClick={() => {
            void onOpenDemandVersions(item);
          }}
        >
          {item.demandVersionCount ?? 0}
        </Button>
      ),
    });
  }
  if (visibleSet.has("itineraryCount")) {
    columnByKey.set("itineraryCount", {
      title: "行程数",
      width: PROJECT_TABLE_COLUMN_WIDTHS.itineraries,
      render: (_, item) => (
        <Button
          type="text"
          size="small"
          aria-label={`查看 ${item.itineraryCount ?? 0} 个行程`}
          onClick={() => onOpenItineraries(item)}
        >
          {item.itineraryCount ?? 0}
        </Button>
      ),
    });
  }
  if (visibleSet.has("projectCreatedAt")) {
    columnByKey.set("projectCreatedAt", {
      title: (
        <SortableHeader
          label="项目创建时间"
          field="project_created_at"
          activeField={activeSortBy}
          activeDirection={activeSortOrder}
          onSort={onSort}
        />
      ),
      dataIndex: "projectCreatedAt",
      width: PROJECT_TABLE_COLUMN_WIDTHS.projectCreatedAt,
      render: (_, item) => renderCompactTime(item.projectCreatedAt, false),
    });
  }
  if (visibleSet.has("versionUpdatedAt")) {
    columnByKey.set("versionUpdatedAt", {
      title: (
        <SortableHeader
          label="版本更新时间"
          field="version_updated_at"
          activeField={activeSortBy}
          activeDirection={activeSortOrder}
          onSort={onSort}
        />
      ),
      dataIndex: "versionUpdatedAt",
      width: PROJECT_TABLE_COLUMN_WIDTHS.versionUpdatedAt,
      render: (_, item) =>
        renderCompactTime(
          item.versionUpdatedAt,
          viewerRole === "dispatcher" && isBeijingToday(item.versionUpdatedAt),
        ),
    });
  }
  if (visibleSet.has("destinations")) {
    columnByKey.set("destinations", {
      title: "目的地",
      width: PROJECT_TABLE_COLUMN_WIDTHS.destination,
      render: (_, item) => buildDestinationText(item),
    });
  }
  if (visibleSet.has("travelerCount")) {
    columnByKey.set("travelerCount", {
      title: "人数",
      width: PROJECT_TABLE_COLUMN_WIDTHS.people,
      render: (_, item) => {
        if (item.studentCount == null && item.teacherCount == null) {
          return "--";
        }
        return `${item.studentCount ?? 0}+${item.teacherCount ?? 0}`;
      },
    });
  }

  return visibleColumns
    .map((key) => columnByKey.get(key))
    .filter((column): column is TableColumnProps<CustomizationProjectListItem> => Boolean(column));
}

/** CRM 风格的升降序双按钮表头。 */
function SortableHeader({
  label,
  field,
  activeField,
  activeDirection,
  onSort,
}: {
  label: string;
  field: CustomizationProjectListSortBy;
  activeField: CustomizationProjectListSortBy;
  activeDirection: CustomizationProjectListSortOrder;
  onSort: (
    field: CustomizationProjectListSortBy,
    direction: CustomizationProjectListSortOrder,
  ) => void;
}) {
  const active = activeField === field;
  return (
    <div className="customization-project-sortable-header">
      <span>{label}</span>
      <span className="customization-project-sort-actions">
        <Button
          size="mini"
          shape="circle"
          aria-label={`${label}升序`}
          type={active && activeDirection === "asc" ? "primary" : "secondary"}
          icon={<ArrowUp size={12} />}
          onClick={(event) => {
            event.stopPropagation();
            onSort(field, "asc");
          }}
        />
        <Button
          size="mini"
          shape="circle"
          aria-label={`${label}降序`}
          type={active && activeDirection === "desc" ? "primary" : "secondary"}
          icon={<ArrowDown size={12} />}
          onClick={(event) => {
            event.stopPropagation();
            onSort(field, "desc");
          }}
        />
      </span>
    </div>
  );
}

/** 渲染压缩时间，并按部门策略突出当天值。 */
function renderCompactTime(value: string | undefined, highlighted: boolean) {
  return (
    <span
      className={`customization-project-time${
        highlighted ? " customization-project-time--today" : ""
      }`}
    >
      {formatBeijingCompactTime(value)}
    </span>
  );
}

/** 将有效状态冲突码转换为列表提示。 */
function buildEffectiveStateConflictText(conflictCode: string): string {
  const labels: Record<string, string> = {
    multiple_orders: "存在多个订单",
    selected_itinerary_not_found: "签约行程不存在",
    itinerary_source_demand_missing: "签约行程缺少来源需求",
    cross_project_itinerary: "签约行程归属冲突",
  };
  return labels[conflictCode] ?? `有效状态冲突：${conflictCode}`;
}

/** 拼接客户来源展示文本。 */
function buildCustomerSourceText(item: CustomizationProjectListItem): string {
  const parts = [item.organizationNameSnapshot, item.customerName].filter(Boolean);
  return parts.length > 0 ? parts.join("-") : "--";
}

/** 推断目的地展示文本。 */
function buildDestinationText(item: CustomizationProjectListItem): string {
  if (item.destinationSummary?.trim()) {
    return item.destinationSummary.trim();
  }
  if (item.tripType && item.durationDays) {
    return `${item.tripType} / ${item.durationDays}天`;
  }
  if (item.tripType) {
    return item.tripType;
  }
  if (item.durationDays) {
    return `${item.durationDays}天`;
  }
  return item.departureStartDate || "--";
}
